import classNames from 'classnames';
import { useEffect, useRef } from 'react';

import styles from './styles.module.scss';

/**
 * iOS-style vertical wheel picker — one column of selectable rows.
 *
 * Originally built for the birth-date modal (HORO-10330); moved to `common/`
 * in HORO-10533 when the Profile v2 birth date and birth time editors became
 * a second and third consumer. The scroll behaviour is unchanged from that
 * implementation; keyboard support is new (see below).
 *
 * The selected row is the one centred in the visible window, with the rows
 * above and below faded so there's feedback while flicking.
 *
 * Tapping or clicking a visible row scrolls that row to the centre and selects it,
 * so the values in view act as shortcuts instead of needing a flick to reach them.
 *
 * Implementation notes:
 *   - Native scroll with `scroll-snap-type: y mandatory`, so flicks settle on
 *     a row without us writing snap maths.
 *   - Padding above and below the list = (VISIBLE_COUNT - 1) / 2 rows, so the
 *     first and last items can still reach the centre.
 *   - Selected index is derived from `scrollTop / ITEM_HEIGHT`, debounced so a
 *     single flick doesn't fire onChange through every row it passes.
 */
const ITEM_HEIGHT = 36;
const VISIBLE_COUNT = 7; // centre + 3 above + 3 below
const PAD_ROWS = (VISIBLE_COUNT - 1) / 2;

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const WheelPicker = ({ items, value, onChange, format = (v) => v, ariaLabel }) => {
    const scrollRef = useRef(null);
    const debounceRef = useRef(null);
    // Set by a row click so the very next position sync animates; every other
    // change (mount, parent update, keyboard) stays an instant jump.
    const animateNextRef = useRef(false);

    // On mount, and when the item list or the externally-held value changes,
    // snap to the selected row without emitting a redundant onChange.
    useEffect(() => {
        const idx = items.indexOf(value);
        if (idx < 0 || !scrollRef.current) return;
        const top = idx * ITEM_HEIGHT;
        if (animateNextRef.current && !prefersReducedMotion() && typeof scrollRef.current.scrollTo === 'function') {
            scrollRef.current.scrollTo({ top, behavior: 'smooth' });
        } else {
            scrollRef.current.scrollTop = top;
        }
        animateNextRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length, value]);

    const handleItemClick = (item) => {
        if (item === value) return;
        animateNextRef.current = true;
        onChange(item);
    };

    const handleScroll = () => {
        if (!scrollRef.current) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (!scrollRef.current) return;
            const idx = Math.round(scrollRef.current.scrollTop / ITEM_HEIGHT);
            const clamped = Math.max(0, Math.min(items.length - 1, idx));
            // Snap once the flick settles, so the picker can't come to rest
            // halfway between two rows.
            scrollRef.current.scrollTop = clamped * ITEM_HEIGHT;
            const next = items[clamped];
            if (next !== value) onChange(next);
        }, 120);
    };

    /**
     * HORO-10533: keyboard operation.
     *
     * The wheel was previously scroll-only — correctly labelled for a screen
     * reader, but impossible to operate without a pointer, which meant a
     * keyboard or switch user could not set their birth date at all. Arrow
     * keys move one row, Home/End jump to the ends; the effect above then
     * scrolls the new value into view, so this stays the single source of
     * truth for position.
     */
    const handleKeyDown = (event) => {
        const currentIndex = items.indexOf(value);
        if (currentIndex < 0) return;

        let nextIndex = null;
        if (event.key === 'ArrowDown') nextIndex = currentIndex + 1;
        if (event.key === 'ArrowUp') nextIndex = currentIndex - 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = items.length - 1;
        if (nextIndex === null) return;

        // Swallow the key only once we know we're handling it, so Tab and
        // anything else still reaches the browser.
        event.preventDefault();
        const clamped = Math.max(0, Math.min(items.length - 1, nextIndex));
        if (items[clamped] !== value) onChange(items[clamped]);
    };

    return (
        <div
            className={styles.wheel}
            style={{ height: ITEM_HEIGHT * VISIBLE_COUNT }}
            role="listbox"
            aria-label={ariaLabel}
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            <div ref={scrollRef} className={styles.wheelScroll} onScroll={handleScroll}>
                <div style={{ height: ITEM_HEIGHT * PAD_ROWS }} aria-hidden="true" />
                {items.map((item) => {
                    const isSelected = item === value;
                    return (
                        // Pointer shortcut only: keyboard operation is on the listbox itself
                        // (arrow keys, Home, End), so rows don't need their own key handlers.
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
                        <div
                            key={item}
                            className={classNames(styles.wheelItem, { [styles.wheelItemSelected]: isSelected })}
                            style={{ height: ITEM_HEIGHT }}
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => handleItemClick(item)}
                        >
                            {format(item)}
                        </div>
                    );
                })}
                <div style={{ height: ITEM_HEIGHT * PAD_ROWS }} aria-hidden="true" />
            </div>
            <div className={styles.wheelHighlight} aria-hidden="true" style={{ height: ITEM_HEIGHT }} />
        </div>
    );
};

export default WheelPicker;
